/**
 * MaintenanceBanner — full-width orange banner shown when the platform is in
 * maintenance mode. Polls /api/health every 30 s; renders only when the
 * response contains `maintenance: true`.
 *
 * Toggle from the server with: bash scripts/stable/maintenance.sh on|off
 */

import React, { useEffect, useState, useCallback } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { healthCheck } from '../../services/api';

const POLL_INTERVAL_MS = 30 * 1000;
const DISMISS_KEY = 'sagax_maintenance_banner_dismissed_msg';

const MaintenanceBanner = () => {
  const [maintenance, setMaintenance] = useState(false);
  const [message, setMessage] = useState('');
  const [dismissed, setDismissed] = useState(false);

  const poll = useCallback(async () => {
    try {
      const res = await healthCheck();
      const data = res?.data || res;
      const inMaint = !!data?.maintenance;
      const msg = data?.message || '';
      setMaintenance(inMaint);
      setMessage(msg);
      // Re-show banner when message changes — admin updated the announcement.
      const lastDismissed = sessionStorage.getItem(DISMISS_KEY);
      if (inMaint && lastDismissed !== msg) {
        setDismissed(false);
      }
      if (!inMaint) {
        sessionStorage.removeItem(DISMISS_KEY);
        setDismissed(false);
      }
    } catch {
      // Health endpoint unreachable — do NOT auto-show banner here.
      // Backend down ≠ planned maintenance; the regular error UI will surface
      // network failures elsewhere. Only the explicit flag triggers the banner.
    }
  }, []);

  useEffect(() => {
    poll();
    const t = setInterval(poll, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [poll]);

  const handleDismiss = () => {
    setDismissed(true);
    if (message) sessionStorage.setItem(DISMISS_KEY, message);
  };

  if (!maintenance || dismissed) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 16px',
        background: '#f59e0b',
        color: '#1f2937',
        fontSize: 13.5,
        lineHeight: 1.5,
        borderBottom: '1px solid #d97706',
        flexShrink: 0,
      }}
    >
      <AlertTriangle size={16} style={{ flexShrink: 0 }} />
      <span style={{ flex: 1, fontWeight: 500 }}>
        {message || '服务器正在维护中，部分功能可能暂时不可用，请稍后再试。'}
      </span>
      <button
        type="button"
        onClick={handleDismiss}
        title="关闭提示"
        aria-label="关闭维护提示"
        style={{
          width: 22,
          height: 22,
          borderRadius: 999,
          border: 'none',
          background: 'transparent',
          color: '#1f2937',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <X size={14} />
      </button>
    </div>
  );
};

export default MaintenanceBanner;
